import { datadogRum, DefaultPrivacyLevel, type RumEvent } from '@datadog/browser-rum';
import { nextjsPlugin } from '@datadog/browser-rum-nextjs';

// Next.js 클라이언트 인스트루먼테이션 진입점(15.3+). 하이드레이션 전에 1회 실행되므로
// Datadog RUM init은 여기서 한다(공식 Next.js 통합 방식). 라우트 추적은 layout의 <DatadogAppRouter/>가 담당.
//
// `@/env`(@t3-oss)를 import하지 않고 process.env를 직접 읽는다 — 이 파일은 번들 최외곽에서
// 가장 먼저 평가돼, env 검증이 throw하면 앱 부팅 자체가 깨질 수 있다. NEXT_PUBLIC_* 값은
// 빌드 타임에 인라인되므로 process.env로 충분하다.

// Sparta 백엔드로 나가는 요청에 trace header를 전파해 RUM↔APM 연결.
const ALLOWED_TRACING_URLS = [
  // api*.spartaclub/codingclub.kr 직접 호출 (백오피스 등 cross-origin 호출).
  /^https:\/\/[^/]*api[^/]*\.sparta(club|codingclub)\.kr/,
  // applicant 앱은 BE를 same-origin 프록시(apply*.kr/v1/*)로 호출한다(next.config rewrites).
  // 이 origin은 위 'api' 정규식에 안 걸려 trace header가 누락됐던 걸 별도 등록으로 보완 —
  // Vercel rewrite가 헤더를 BE로 전달하므로 same-origin이어도 APM과 연결된다.
  /^https:\/\/[^/]*apply[^/]*\.(spartaclub|codingclub)\.kr\/v1\//,
];

function getSampleRate(value: string | undefined): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

const applicationId = process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID;
const clientToken = process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN;
const datadogEnv = process.env.NEXT_PUBLIC_DATADOG_ENV;

// RUM Application이 아직 없어 키가 비어 있는 환경(local·preview)에서는 init을 건너뛴다.
if (applicationId && clientToken) {
  datadogRum.init({
    applicationId,
    clientToken,
    site: 'datadoghq.com',
    service: process.env.NEXT_PUBLIC_DATADOG_SERVICE,
    env: datadogEnv,
    // Vercel이 주입하는 커밋 SHA 앞 7자리 — sourcemap의 release-version과 일치해야 stack trace가 복원된다.
    version: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA?.substring(0, 7),
    allowedTracingUrls: ALLOWED_TRACING_URLS,
    traceSampleRate: getSampleRate(process.env.NEXT_PUBLIC_DATADOG_TRACE_SAMPLE_RATE),
    sessionSampleRate: getSampleRate(process.env.NEXT_PUBLIC_DATADOG_SESSION_RATE),
    sessionReplaySampleRate: getSampleRate(process.env.NEXT_PUBLIC_DATADOG_SESSION_REPLAY_RATE),
    // 리플레이 자동 시작 끔 — staff 식별 후 비-staff일 때만 useInitDatadogUser가 수동 시작한다.
    // (자동 시작 시 식별 전 구간이 녹화돼 staff 세션에도 리플레이가 남던 문제 방지)
    startSessionReplayRecordingManually: true,
    trackResources: true,
    trackUserInteractions: true,
    trackLongTasks: true,
    // prod에서만 입력값 마스킹(개인정보), 그 외 환경은 디버깅 편의를 위해 그대로 노출.
    defaultPrivacyLevel:
      datadogEnv === 'prod' ? DefaultPrivacyLevel.MASK_USER_INPUT : DefaultPrivacyLevel.ALLOW,
    beforeSend: (event: RumEvent) => {
      // 서드파티 태그 내부 에러는 우리가 코드로 못 고치는 노이즈라 RUM에 올리지 않는다.
      // - Amplitude SDK 내부 에러 (CPL이 Amplitude를 직접 다룬다)
      // - GTM에 설정된 kakaoPixel 태그의 잘못된 메서드 호출(예: ViewCart) — 근본 수정은 GTM 콘솔에서.
      if (event.type === 'error') {
        const { message } = event.error;
        if (message.includes('Amplitude') || message.includes('kakaoPixel')) {
          return false;
        }
      }
      return true;
    },
    // Next.js App Router 라우트 변경·view 이름 정규화 자동 추적.
    plugins: [nextjsPlugin()],
  });
}
